import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Building, Stack, Database, UsersFour, CheckCircle, ArrowRight, Sparkle, UploadSimple, Trash } from '@phosphor-icons/react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'warehouse', label: 'Warehouse', icon: Building },
  { key: 'locations', label: 'Locations', icon: Stack },
  { key: 'products',  label: 'Products',  icon: Database },
  { key: 'team',      label: 'Team',      icon: UsersFour },
  { key: 'done',      label: 'Finish',    icon: Sparkle },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { company, refresh } = useCompany();
  const [idx, setIdx] = useState(0);
  const [status, setStatus] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/onboarding/status`, { withCredentials: true })
      .then((r) => setStatus(r.data.steps || {}))
      .catch(() => {});
  }, []);

  const markStep = async (step, metadata) => {
    try {
      await axios.post(`${API}/api/onboarding/step`, { step, metadata }, { withCredentials: true });
    } catch { /* non-blocking */ }
  };

  const finish = async () => {
    await axios.post(`${API}/api/onboarding/complete`, {}, { withCredentials: true });
    await refresh();
    navigate('/', { replace: true });
    toast.success('Welcome aboard — your warehouse is ready!');
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-3xl mx-auto" data-testid="onboarding-page">
        <div>
          <h1 className="text-3xl font-bold">
            <Sparkle size={24} weight="fill" className="inline text-brand mr-2" />
            Welcome, {company?.name || 'there'}
          </h1>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Let's get your warehouse live in 2 minutes</p>
        </div>

        {/* Step rail */}
        <div className="flex items-center gap-2" data-testid="onboarding-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = status[s.key]?.done_at;
            const current = i === idx;
            return (
              <React.Fragment key={s.key}>
                <button
                  onClick={() => setIdx(i)}
                  data-testid={`step-${s.key}`}
                  className={`flex items-center gap-2 px-3 py-2 border transition text-xs uppercase tracking-widest font-bold ${
                    done ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : current ? 'border-brand bg-brand text-white'
                    : 'border-border text-muted-foreground hover:border-slate-500'
                  }`}
                >
                  {done ? <CheckCircle size={14} weight="fill" /> : <Icon size={14} weight="bold" />}
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="bg-card border border-border rounded-lg p-8" data-testid={`onboarding-step-${STEPS[idx].key}`}>
          {STEPS[idx].key === 'warehouse' && <WarehouseStep onDone={async () => { await markStep('warehouse'); setStatus((s) => ({ ...s, warehouse: { done_at: new Date().toISOString() } })); setIdx(1); }} />}
          {STEPS[idx].key === 'locations' && <LocationsStep onDone={async () => { await markStep('locations'); setStatus((s) => ({ ...s, locations: { done_at: new Date().toISOString() } })); setIdx(2); }} onSkip={() => setIdx(2)} />}
          {STEPS[idx].key === 'products' && <ProductsStep onDone={async () => { setStatus((s) => ({ ...s, products: { done_at: new Date().toISOString() } })); setIdx(3); }} onSkip={() => setIdx(3)} />}
          {STEPS[idx].key === 'team' && <TeamStep onDone={async () => { await markStep('team'); setStatus((s) => ({ ...s, team: { done_at: new Date().toISOString() } })); setIdx(4); }} onSkip={() => setIdx(4)} />}
          {STEPS[idx].key === 'done' && <FinishStep onFinish={finish} />}
        </div>

        <div className="flex justify-between text-xs text-muted-foreground font-mono">
          <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="uppercase tracking-widest disabled:opacity-40">← Back</button>
          <button onClick={finish} className="uppercase tracking-widest hover:text-brand" data-testid="onboarding-skip-all">Skip for now</button>
        </div>
      </div>
    </Layout>
  );
}

// ----- STEP: Warehouse -----
const WarehouseStep = ({ onDone }) => {
  const [form, setForm] = useState({ name: 'Main Warehouse', code: 'MAIN', address: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/warehouses`, form, { withCredentials: true });
      toast.success('Warehouse created');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create warehouse');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Where are you shipping from?</h2>
      <p className="text-sm text-muted-foreground">Add your first warehouse. You can add more later.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Name</Label>
          <Input data-testid="wh-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label className="overline">Code</Label>
          <Input data-testid="wh-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </div>
      </div>
      <div>
        <Label className="overline">Address</Label>
        <Input data-testid="wh-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="1 Loading Dock Rd, Sydney NSW" />
      </div>
      <Button data-testid="wh-save" onClick={save} disabled={busy} className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">{busy ? 'Saving…' : 'Create & continue'} <ArrowRight size={14} weight="bold" className="ml-2" /></Button>
    </div>
  );
};

// ----- STEP: Locations -----
const LocationsStep = ({ onDone, onSkip }) => (
  <div className="space-y-4">
    <h2 className="text-xl font-bold">Rack & bin layout</h2>
    <p className="text-sm text-muted-foreground">You can scan barcodes later — for now, continue with our default template (6 bins) or skip this step.</p>
    <div className="bg-muted border border-border p-4 font-mono text-xs">
      A-01-01 · A-01-02 · A-02-01 · B-01-01 · B-01-02 · RECV-01
    </div>
    <div className="flex gap-2">
      <Button data-testid="loc-accept" onClick={onDone} className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">
        Use default layout <ArrowRight size={14} weight="bold" className="ml-2" />
      </Button>
      <Button data-testid="loc-skip" onClick={onSkip} variant="outline" className="rounded-none">Skip</Button>
    </div>
  </div>
);

// ----- STEP: Products -----
const ProductsStep = ({ onDone, onSkip }) => {
  const [busy, setBusy] = useState(null);
  const [file, setFile] = useState(null);

  const seed = async () => {
    setBusy('seed');
    try {
      const { data } = await axios.post(`${API}/api/onboarding/seed-demo-data`, {}, { withCredentials: true });
      toast.success(`${data.skus_inserted} demo SKUs created`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };
  const clone = async () => {
    setBusy('clone');
    try {
      const { data } = await axios.post(`${API}/api/onboarding/clone-from-stockaris`, {}, { withCredentials: true });
      toast.success(`${data.skus_inserted} SKUs cloned from Stockaris Demo`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };
  const importCsv = async () => {
    if (!file) { toast.error('Select a CSV first'); return; }
    setBusy('csv');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/onboarding/import-csv`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${data.inserted} SKUs imported (${data.error_count} errors)`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Load your products</h2>
      <p className="text-sm text-muted-foreground">Pick the fastest path — you can always add more SKUs later from the Inventory page.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <OptionCard
          dataTestid="option-seed"
          icon={Sparkle} title="Seed demo data"
          desc="8 sample SKUs + 6 bins so you can try every feature immediately."
          busy={busy === 'seed'} onClick={seed}
        />
        <OptionCard
          dataTestid="option-clone"
          icon={Database} title="Clone from Stockaris Demo"
          desc="Copy a curated 10-SKU starter set from our official demo tenant."
          busy={busy === 'clone'} onClick={clone}
        />
        <div className="border border-border p-4" data-testid="option-csv">
          <UploadSimple size={22} weight="bold" className="text-brand" />
          <div className="font-bold mt-2 text-sm">Upload CSV</div>
          <p className="text-xs text-muted-foreground mt-1">Columns: sku_code, name, unit_price, stock_qty, uom, barcode</p>
          <input data-testid="csv-input" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-3 text-xs w-full" />
          <Button data-testid="csv-upload" onClick={importCsv} disabled={!file || busy === 'csv'} className="w-full mt-3 bg-brand hover:bg-brand/90 text-white rounded-none font-bold text-xs">
            {busy === 'csv' ? 'Uploading…' : 'Import CSV'}
          </Button>
        </div>
      </div>
      <button onClick={onSkip} className="text-xs text-muted-foreground uppercase tracking-widest hover:text-brand" data-testid="products-skip">
        Skip — I'll add products later
      </button>
    </div>
  );
};

const OptionCard = ({ dataTestid, icon: Icon, title, desc, busy, onClick }) => (
  <button
    data-testid={dataTestid}
    onClick={onClick}
    disabled={busy}
    className="border border-border p-4 text-left hover:border-brand transition disabled:opacity-50"
  >
    <Icon size={22} weight="bold" className="text-brand" />
    <div className="font-bold mt-2 text-sm">{title}</div>
    <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    <div className="mt-3 text-xs uppercase tracking-widest font-bold text-brand">
      {busy ? 'Working…' : 'Choose →'}
    </div>
  </button>
);

// ----- STEP: Team -----
const TeamStep = ({ onDone, onSkip }) => {
  const [invites, setInvites] = useState([{ email: '', role: 'picker' }]);
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const clean = invites.filter((i) => i.email && i.email.includes('@'));
    if (!clean.length) { toast.error('Add at least one email'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/invites`,
        { invites: clean, app_url: window.location.origin },
        { withCredentials: true });
      setSent(data.results || []);
      toast.success(`${(data.results || []).filter((r) => r.status === 'sent').length} invites sent`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Invite your team</h2>
      <p className="text-sm text-muted-foreground">Stockaris is better with your team. We'll email them a one-click invite link (14 days to accept).</p>
      <div className="space-y-2" data-testid="invite-rows">
        {invites.map((inv, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              data-testid={`invite-email-${i}`}
              placeholder="email@company.com"
              value={inv.email}
              onChange={(e) => setInvites(invites.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
              className="flex-1"
            />
            <select
              data-testid={`invite-role-${i}`}
              value={inv.role}
              onChange={(e) => setInvites(invites.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
              className="border border-border py-2 px-3 text-sm font-mono uppercase"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="picker">Picker</option>
              <option value="packer">Packer</option>
              <option value="viewer">Viewer</option>
            </select>
            <button onClick={() => setInvites(invites.filter((_, j) => j !== i))} className="p-1 text-muted-foreground hover:text-rose-600"><Trash size={16} weight="bold" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setInvites([...invites, { email: '', role: 'picker' }])} data-testid="invite-add-row" className="text-xs uppercase tracking-widest text-brand font-bold hover:underline">+ Add another</button>
      {sent.length > 0 && (
        <div className="text-xs font-mono space-y-1 border-l-2 border-emerald-500 pl-3">
          {sent.map((s) => <div key={s.email} data-testid={`sent-${s.email}`}>✉ {s.email} · {s.status}</div>)}
        </div>
      )}
      <div className="flex gap-2">
        <Button data-testid="invite-send" onClick={send} disabled={busy} className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">
          {busy ? 'Sending…' : (sent.length ? 'Resend' : 'Send invites')}
        </Button>
        {sent.length > 0 && (
          <Button data-testid="invite-continue" onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold">
            Continue <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        )}
        <Button data-testid="invite-skip" onClick={onSkip} variant="outline" className="rounded-none">Skip</Button>
      </div>
      <p className="text-[10px] text-muted-foreground font-mono uppercase">* Emails are MOCKED until Resend API key is configured. Invite links will appear in the sent list above.</p>
    </div>
  );
};

const FinishStep = ({ onFinish }) => (
  <div className="text-center py-10">
    <div className="w-16 h-16 bg-emerald-500 mx-auto flex items-center justify-center mb-4">
      <CheckCircle size={36} weight="fill" className="text-white" />
    </div>
    <h2 className="text-2xl font-bold">You're all set!</h2>
    <p className="text-sm text-muted-foreground mt-2">Your warehouse is live. Jump into the dashboard to start receiving goods, picking orders, and monitoring KPIs.</p>
    <Button data-testid="onboarding-done-btn" onClick={onFinish} className="mt-6 bg-brand hover:bg-brand/90 text-white rounded-none font-bold px-8 py-3">
      Go to dashboard <ArrowRight size={16} weight="bold" className="ml-2" />
    </Button>
  </div>
);
